import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchEmployees, saveEmployee, deleteEmployee } from '../services/employeeApi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common/ToastProvider';
// import { hashPassword } from '../utils/bcryptUtils'; // We'll need to create this or use a different approach

/**
 * Employee Management Page (Admin Only)
 * Allows admin to add, edit, and delete employees
 */
const EmployeeManagement = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    user_name: '',
    full_name: '',
    email: '',
    password: '',
    contact_number: '',
    designation: '',
    department: '',
    employee_status: 'Active',
    is_admin: false,
    is_super_admin: false,
  });
  const [errors, setErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      showToast('Access denied. Admin privileges required.', 'error');
      navigate('/');
    }
  }, [isAdmin, navigate, showToast]);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchEmployees();
      if (response.success) {
        setEmployees(response.data || []);
      } else {
        showToast(response.error || 'Failed to load employees', 'error');
      }
    } catch (error) {
      showToast('Error loading employees', 'error');
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (isAdmin) {
      loadEmployees();
    }
  }, [isAdmin, loadEmployees]);

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        employee_id: employee.employee_id || '',
        user_name: employee.user_name || '',
        full_name: employee.full_name || '',
        email: employee.email || '',
        password: '', // Don't show existing password
        contact_number: employee.contact_number || '',
        designation: employee.designation || '',
        department: employee.department || '',
        employee_status: employee.employee_status || 'Active',
        is_admin: employee.is_admin || false,
        is_super_admin: employee.is_super_admin || false,
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        employee_id: '',
        user_name: '',
        full_name: '',
        email: '',
        password: '',
        contact_number: '',
        designation: '',
        department: '',
        employee_status: 'Active',
        is_admin: false,
        is_super_admin: false,
      });
    }
    setErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
    setFormData({
      employee_id: '',
      user_name: '',
      full_name: '',
      email: '',
      password: '',
      contact_number: '',
      designation: '',
      department: '',
      employee_status: 'Active',
      is_admin: false,
      is_super_admin: false,
    });
    setErrors({});
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.employee_id.trim()) {
      newErrors.employee_id = 'Employee ID is required';
    }

    if (!editingEmployee && !formData.password) {
      newErrors.password = 'Password is required for new employees';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const employeeData = { ...formData };

      // Only include password if it's provided (for new employees or password reset)
      if (!employeeData.password) {
        delete employeeData.password;
      }

      // Remove password from data sent to API (backend will handle hashing)
      // Actually, we need to send password to backend, but backend should hash it
      const response = editingEmployee
        ? await saveEmployee(employeeData, editingEmployee._id)
        : await saveEmployee(employeeData);

      if (response.success) {
        showToast(
          editingEmployee ? 'Employee updated successfully' : 'Employee created successfully',
          'success'
        );
        handleCloseDialog();
        loadEmployees();
      } else {
        showToast(response.error || 'Failed to save employee', 'error');
      }
    } catch (error) {
      showToast('Error saving employee', 'error');
      console.error('Error saving employee:', error);
    }
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;

    try {
      const response = await deleteEmployee(employeeToDelete._id);
      if (response.success) {
        showToast('Employee deleted successfully', 'success');
        setDeleteDialogOpen(false);
        setEmployeeToDelete(null);
        loadEmployees();
      } else {
        showToast(response.error || 'Failed to delete employee', 'error');
      }
    } catch (error) {
      showToast('Error deleting employee', 'error');
      console.error('Error deleting employee:', error);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Employee Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="small"
        >
          Add Employee
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee ID</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No employees found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((employee) => (
                  <TableRow key={employee._id}>
                    <TableCell>{employee.employee_id}</TableCell>
                    <TableCell>{employee.full_name || employee.user_name || '-'}</TableCell>
                    <TableCell>{employee.email || '-'}</TableCell>
                    <TableCell>{employee.contact_number || '-'}</TableCell>
                    <TableCell>{employee.designation || '-'}</TableCell>
                    <TableCell>{employee.department || '-'}</TableCell>
                    <TableCell>{employee.employee_status || 'Active'}</TableCell>
                    <TableCell>
                      {employee.is_super_admin
                        ? 'Super Admin'
                        : employee.is_admin
                        ? 'Admin'
                        : 'Employee'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(employee)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(employee)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Employee Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Employee ID"
              value={formData.employee_id}
              onChange={(e) => handleFieldChange('employee_id', e.target.value)}
              fullWidth
              required
              disabled={!!editingEmployee}
              error={!!errors.employee_id}
              helperText={errors.employee_id}
            />
            <TextField
              label="User Name"
              value={formData.user_name}
              onChange={(e) => handleFieldChange('user_name', e.target.value)}
              fullWidth
            />
            <TextField
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => handleFieldChange('full_name', e.target.value)}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              label={editingEmployee ? 'New Password (leave blank to keep current)' : 'Password'}
              type="password"
              value={formData.password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              fullWidth
              required={!editingEmployee}
              error={!!errors.password}
              helperText={errors.password}
            />
            <TextField
              label="Contact Number"
              value={formData.contact_number}
              onChange={(e) => handleFieldChange('contact_number', e.target.value)}
              fullWidth
            />
            <TextField
              label="Designation"
              value={formData.designation}
              onChange={(e) => handleFieldChange('designation', e.target.value)}
              fullWidth
            />
            <TextField
              label="Department"
              value={formData.department}
              onChange={(e) => handleFieldChange('department', e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Status"
              value={formData.employee_status}
              onChange={(e) => handleFieldChange('employee_status', e.target.value)}
              fullWidth
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="On Leave">On Leave</MenuItem>
              <MenuItem value="Resigned">Resigned</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Is Admin"
                select
                value={formData.is_admin ? 'true' : 'false'}
                onChange={(e) => handleFieldChange('is_admin', e.target.value === 'true')}
                fullWidth
              >
                <MenuItem value="false">No</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
              </TextField>
              <TextField
                label="Is Super Admin"
                select
                value={formData.is_super_admin ? 'true' : 'false'}
                onChange={(e) => handleFieldChange('is_super_admin', e.target.value === 'true')}
                fullWidth
              >
                <MenuItem value="false">No</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingEmployee ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete employee {employeeToDelete?.employee_id}? This action
            cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmployeeManagement;
